import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { applyConsent, lookupRegion, OPEN_SETTINGS_EVENT, readConsent, saveConsent, STORAGE_KEY } from "@/lib/cookieConsent";

const copy = {
  en: {
    title: "Cookies on this website",
    body: "With your permission, we use Google Analytics and Google Ads to see how visitors find and use this site. The Best Practice app itself uses no tracking. You can change your choice at any time under Cookie settings.",
    accept: "Accept",
    reject: "Reject",
    more: "Privacy policy",
  },
  de: {
    title: "Cookies auf dieser Website",
    body: "Mit deiner Erlaubnis nutzen wir Google Analytics und Google Ads, um zu sehen, wie Besucher:innen diese Website finden und nutzen. Die Best Practice App selbst verwendet kein Tracking. Du kannst deine Wahl jederzeit unter Cookie-Einstellungen ändern.",
    accept: "Akzeptieren",
    reject: "Ablehnen",
    more: "Datenschutzerklärung",
  },
};

export function CookieBanner() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const t = pathname === "/de" || pathname.startsWith("/de/") ? copy.de : copy.en;

  useEffect(() => {
    let alive = true;
    if (!readConsent()) {
      lookupRegion().then(({ required }) => {
        if (alive && required && !readConsent()) setOpen(true);
      });
    }
    const onOpen = () => setOpen(true);
    const onStorage = (e: StorageEvent) => {
      if (e.key !== STORAGE_KEY) return;
      const rec = readConsent();
      if (rec) { applyConsent(rec.decision); setOpen(false); }
    };
    window.addEventListener(OPEN_SETTINGS_EVENT, onOpen);
    window.addEventListener("storage", onStorage);
    return () => {
      alive = false;
      window.removeEventListener(OPEN_SETTINGS_EVENT, onOpen);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  if (!open) return null;

  const choose = async (d: "granted" | "denied") => {
    await saveConsent(d);
    setOpen(false);
  };

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-labelledby="cookie-banner-title"
      className="fixed inset-x-3 bottom-3 z-[60] mx-auto max-w-xl rounded-2xl border bg-card p-5 text-card-foreground shadow-lg sm:bottom-5"
    >
      <p id="cookie-banner-title" className="font-semibold">{t.title}</p>
      <p className="mt-2 text-sm text-muted-foreground">
        {t.body}{" "}
        <Link to="/datenschutz" className="text-primary underline-offset-2 hover:underline">{t.more}</Link>
      </p>
      <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:justify-end">
        <Button variant="outline" onClick={() => choose("denied")}>{t.reject}</Button>
        <Button onClick={() => choose("granted")}>{t.accept}</Button>
      </div>
    </div>
  );
}
